import { FC, useCallback, useEffect, useRef } from "react";
import classNames from "classnames";

import { ClientActionsMenuProps } from "./types";

import styles from "./ClientActionsMenu.module.css";

const ClientActionsMenu: FC<ClientActionsMenuProps> = ({
  client,
  isOpen,
  onOpenChange,
  onActAs,
  onRename,
  onDetach,
}) => {
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onOpenChange(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, onOpenChange]);

  const handleToggle = useCallback(() => {
    onOpenChange(!isOpen);
  }, [isOpen, onOpenChange]);

  const handleActAs = useCallback(() => {
    onOpenChange(false);
    onActAs?.(client);
  }, [client, onActAs, onOpenChange]);

  const handleDetach = useCallback(() => {
    onOpenChange(false);
    onDetach?.(client);
  }, [client, onDetach, onOpenChange]);

  const handleRename = useCallback(() => {
    onOpenChange(false);
    onRename?.(client);
  }, [client, onRename, onOpenChange]);

  return (
    <div className={styles["menu"]} ref={menuRef}>
      <button
        className={styles["menu__trigger"]}
        onClick={handleToggle}
        type="button"
        aria-label="Client actions"
        aria-expanded={isOpen}
        aria-haspopup="menu"
      >
        ⋯
      </button>
      {isOpen && (
        <div className={styles["menu__dropdown"]} role="menu">
          {onActAs && (
            <button
              className={styles["menu__item"]}
              onClick={handleActAs}
              type="button"
              role="menuitem"
            >
              Enter Account
            </button>
          )}
          {onRename && (
            <button
              className={styles["menu__item"]}
              onClick={handleRename}
              type="button"
              role="menuitem"
            >
              Rename
            </button>
          )}
          {onDetach && (
            <button
              className={classNames(styles["menu__item"], styles["is-danger"])}
              onClick={handleDetach}
              type="button"
              role="menuitem"
            >
              Delete Client
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default ClientActionsMenu;
